import { useState, useEffect } from "react";
import type { Camper, PaginatedResponse } from "../types";
import { campersApi, type AttendanceFilter } from "../api/campers";

interface UseCampersOptions {
  page?: number;
  pageSize?: number;
  search?: string;
  cabinId?: number;
  year?: number;
  termId?: number;
  divisionId?: number;
  divisionIds?: number[];
  team?: string;
  attendance?: AttendanceFilter;
  /** Bump to force a refetch without changing any filter. */
  refreshKey?: number;
}

export function useCampers(options: UseCampersOptions = {}) {
  const { page = 1, pageSize = 20, search, cabinId, year, termId, divisionId, divisionIds, team, attendance, refreshKey } = options;
  const [data, setData] = useState<PaginatedResponse<Camper> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Stable key for the array dep so we don't refetch on identity changes alone.
  const divisionIdsKey = divisionIds ? divisionIds.slice().sort((a, b) => a - b).join(",") : "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    campersApi
      .list({ page, pageSize, search, cabinId, year, termId, divisionId, divisionIds, team, attendance })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, cabinId, year, termId, divisionId, divisionIdsKey, team, attendance, refreshKey]);

  return { data, loading, error, refetch: () => {} };
}

export function useCamper(id: number) {
  const [camper, setCamper] = useState<Camper | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    campersApi
      .get(id)
      .then(setCamper)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id, refreshKey]);

  return { camper, loading, error, refetch: () => setRefreshKey((k) => k + 1) };
}
